import React, { useCallback, useMemo } from 'react';
import cx from 'classnames';
import { useStandardCollator } from 'js/utils/string';

import { Button } from 'reactstrap';
import { Link } from 'react-router-dom';
import { Icon, LimitedList } from 'js/components';

import styles from './UserGroupsDashboard.module.scss';

const MAX_LIST_ITEMS = 5;

function UserGroupCard({
  userGroup,
  workspace,
  isFavorited,
  onToggleFavorite,
  onArchive,
  onRestore,
}) {
  const isArchived = userGroup.archived_state === 'archived';
  const collator = useStandardCollator();

  const capabilities = useMemo(() => {
    const capabilitySets = userGroup?.capability_sets ?? [];
    const byId = new Map();
    capabilitySets.forEach((capabilitySet) => {
      (capabilitySet.capabilities ?? []).forEach((capability) => {
        byId.set(capability.id, capability);
      });
    });
    return Array.from(byId.values()).sort(
      (a, b) => collator.compare(a.name, b.name) || collator.compare(a.id, b.id)
    );
  }, [userGroup?.capability_sets, collator]);

  const handleArchive = useCallback(async () => {
    await onArchive(userGroup);
  }, [onArchive, userGroup]);

  const handleRestore = useCallback(async () => {
    await onRestore(userGroup);
  }, [onRestore, userGroup]);

  const handleStarClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      onToggleFavorite?.(userGroup.id, isFavorited);
    },
    [onToggleFavorite, userGroup.id, isFavorited]
  );

  return (
    <div
      className={cx(styles['user-group-card'], {
        favorite: isFavorited,
      })}
    >
      <div
        className="stamp mr-1"
        onClick={handleStarClick}
        title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
      >
        <span className="fa-layers fa-fw">
          <Icon icon="star" className="background-layer" />
          <Icon icon={['far', 'star']} />
        </span>
      </div>
      <div>
        <Link
          to={`/w/${workspace.id}/pm/user-groups/${userGroup.id}`}
          className="text-decoration-none"
        >
          {userGroup.name}
        </Link>
        <p
          className={cx(
            'text-muted line-clamp-3',
            styles['user-group-description']
          )}
        >
          {userGroup.description}
        </p>
      </div>
      <div className={styles['user-group-capabilities']}>
        <LimitedList
          items={capabilities}
          maxItems={MAX_LIST_ITEMS}
          itemKey={(capability) => capability.id}
          renderItem={(capability) => capability.name}
        />
      </div>
      <div className="actions">
        {isArchived ? (
          <Button
            color="secondary"
            size="sm"
            className="btn-icon"
            onClick={handleRestore}
          >
            <Icon icon="box-open" />
          </Button>
        ) : (
          <Button
            color="secondary"
            size="sm"
            className="btn-icon"
            onClick={handleArchive}
          >
            <Icon icon="archive" className="btn-icon" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default UserGroupCard;
